import type {
  BenefitsHealthcareEnrollmentPlanFragment,
  BenefitsValueObjectsBenefitMembersMemberTypeEnum,
  BenefitsValueObjectsBenefitMembersSexEnum,
  BulkCreateBenefitsHealthcareEnrollmentNewProfilesInput,
} from 'generated/resources/graphql'

// Interface for the form that extends the GraphQL input type
export type BenefitMemberEnrollmentData = Omit<
  BulkCreateBenefitsHealthcareEnrollmentNewProfilesInput,
  'companyId' | 'employeeId'
>

export type PersonalInformationFormData = Record<
  keyof BenefitMemberEnrollmentData,
  string | undefined
>

export interface HealthcareEnrollmentData {
  identifierTypes: IdentifierType[]
  healthcarePlanData: BenefitsHealthcareEnrollmentPlanFragment
  personalInformationData: PersonalInformationFormData
}
export interface IdentifierType {
  label: string
  value: string
}

// Guarantees exhaustiveness
const SEX_VALUES: Record<BenefitsValueObjectsBenefitMembersSexEnum, boolean> = {
  male: true,
  female: true,
}

// Guarantees exhaustiveness
const MEMBER_TYPE_VALUES: Record<BenefitsValueObjectsBenefitMembersMemberTypeEnum, boolean> = {
  employee: true,
  spouse: true,
  child: true,
  partner: true,
  other_family: true,
  other_non_family: true,
}

// Type predicates for enum validation
export const isSexValue = (val: string): val is BenefitsValueObjectsBenefitMembersSexEnum =>
  Object.keys(SEX_VALUES).includes(val)

export const isMemberTypeValue = (
  val: string
): val is BenefitsValueObjectsBenefitMembersMemberTypeEnum =>
  Object.keys(MEMBER_TYPE_VALUES).includes(val)

// Tagged template helper with type predicate for BenefitMemberEnrollmentData
export const benefitEnrollmentField = (
  strings: TemplateStringsArray,
  ...values: unknown[]
): keyof BenefitMemberEnrollmentData => {
  // Ensure we only have one string part and no values for simple field names
  if (strings.length !== 2 || values.length !== 1) {
    throw new Error(
      'benefitEnrollmentField should be used with a single field name: benefitEnrollmentField`${fieldName}`'
    )
  }

  const fieldName = String.raw(strings, ...values)

  // Type predicate to ensure the field exists on BenefitMemberEnrollmentData
  const isValidField = (field: string): field is keyof BenefitMemberEnrollmentData => {
    switch (field) {
      case 'firstName':
      case 'lastName':
      case 'email':
      case 'phoneNumber':
      case 'addressLine1':
      case 'addressLine2':
      case 'birthday':
      case 'postalCode':
      case 'city':
      case 'province':
      case 'taxIdNumber':
      case 'taxIdType':
      case 'sex':
      case 'iban':
      case 'memberType':
        return true
      default:
        return false
    }
  }

  if (!isValidField(fieldName)) {
    throw new Error(`Invalid field name: ${fieldName}`)
  }

  return fieldName
}
